import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { User, LogOut, Package, CreditCard, Calendar, DollarSign, Settings, FileText, Download, Eye } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function UserDashboard() {
  const navigate = useNavigate();
  const [subscriptions, setSubscriptions] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const userName = localStorage.getItem('user_name');
  const userEmail = localStorage.getItem('user_email');

  useEffect(() => {
    const token = localStorage.getItem('user_token');
    if (!token) {
      navigate('/user/login');
      return;
    }
    fetchUserData();
  }, [navigate]);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('user_token');
    return {
      headers: {
        Authorization: `Bearer ${token}`
      }
    };
  };

  const fetchUserData = async () => {
    try {
      const [subsRes, transRes] = await Promise.all([
        axios.get(`${API}/user/subscriptions`, getAuthHeaders()),
        axios.get(`${API}/user/transactions`, getAuthHeaders())
      ]);
      setSubscriptions(subsRes.data);
      setTransactions(transRes.data.filter(t => t.payment_status === 'paid'));
    } catch (error) {
      toast.error('Failed to load data');
      if (error.response?.status === 401) {
        navigate('/user/login');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('user_token');
    localStorage.removeItem('user_email');
    localStorage.removeItem('user_name');
    navigate('/user/login');
  };

  const handleSetPassword = async () => {
    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    if (newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    try {
      await axios.post(`${API}/user/set-password`, { password: newPassword }, getAuthHeaders());
      toast.success('Password set successfully!');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error) {
      toast.error('Failed to set password');
    }
  };

  const handleViewInvoice = async (transactionId) => {
    try {
      const response = await axios.get(`${API}/user/invoice/${transactionId}/details`, getAuthHeaders());
      setSelectedInvoice(response.data);
      setShowInvoiceModal(true);
    } catch (error) {
      toast.error('Failed to load invoice');
    }
  };

  const downloadInvoice = async () => {
    if (!selectedInvoice) return;
    
    try {
      const token = localStorage.getItem('user_token');
      const response = await axios.get(
        `${API}/user/invoice/${selectedInvoice.transaction.id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`
          },
          responseType: 'blob'
        }
      );
      
      // Create a blob from the PDF data
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      
      // Create a temporary link and trigger download
      const a = document.createElement('a');
      a.href = url;
      a.download = `Invoice_${selectedInvoice.transaction.id.substring(0, 8).toUpperCase()}.pdf`;
      document.body.appendChild(a);
      a.click();
      
      // Cleanup
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      
      toast.success('Invoice PDF downloaded!');
    } catch (error) {
      console.error('Error downloading invoice:', error);
      toast.error('Failed to download invoice');
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getNextBillingDate = (sub) => {
    if (sub.next_billing_date) {
      return formatDate(sub.next_billing_date);
    }
    return 'N/A';
  };

  const calculateUpcoming = () => {
    return subscriptions.reduce((sum, sub) => sum + sub.amount, 0);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading your account...</p>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="user-dashboard" className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center">
            <User className="w-6 h-6 text-indigo-600 mr-3" />
            <div>
              <h1 className="text-xl font-semibold text-gray-900">{userName}</h1>
              <p className="text-sm text-gray-600">{userEmail}</p>
            </div>
          </div>
          <Button
            data-testid="logout-button"
            onClick={handleLogout}
            variant="outline"
            className="border-gray-300"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Logout
          </Button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        <Tabs defaultValue="overview" className="space-y-6">
          <TabsList>
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="subscriptions">Subscriptions</TabsTrigger>
            <TabsTrigger value="payments">Payments & Invoices</TabsTrigger>
            <TabsTrigger value="settings">Settings</TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview">
            {/* Stats */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600">Active Subscriptions</p>
                      <p className="text-3xl font-bold text-indigo-600">{subscriptions.length}</p>
                    </div>
                    <Package className="w-12 h-12 text-indigo-600 opacity-20" />
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600">Total Paid</p>
                      <p className="text-3xl font-bold text-green-600">
                        ${transactions.reduce((sum, t) => sum + t.amount, 0).toFixed(2)}
                      </p>
                    </div>
                    <DollarSign className="w-12 h-12 text-green-600 opacity-20" />
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600">Upcoming Payments</p>
                      <p className="text-3xl font-bold text-orange-600">
                        ${calculateUpcoming().toFixed(2)}
                      </p>
                    </div>
                    <Calendar className="w-12 h-12 text-orange-600 opacity-20" />
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Quick Summary */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle>Recent Subscriptions</CardTitle>
                </CardHeader>
                <CardContent>
                  {subscriptions.slice(0, 3).map((sub) => (
                    <div key={sub.id} className="mb-4 pb-4 border-b last:border-0">
                      <p className="font-semibold">{sub.package_details?.name}</p>
                      <p className="text-sm text-gray-600">Next billing: {getNextBillingDate(sub)}</p>
                    </div>
                  ))}
                  {subscriptions.length === 0 && (
                    <p className="text-gray-500 text-center py-4">No subscriptions yet</p>
                  )}
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Recent Payments</CardTitle>
                </CardHeader>
                <CardContent>
                  {transactions.slice(0, 3).map((trans) => (
                    <div key={trans.id} className="mb-4 pb-4 border-b last:border-0">
                      <div className="flex justify-between">
                        <p className="font-semibold">${trans.amount.toFixed(2)}</p>
                        <p className="text-sm text-gray-600">{formatDate(trans.created_at)}</p>
                      </div>
                    </div>
                  ))}
                  {transactions.length === 0 && (
                    <p className="text-gray-500 text-center py-4">No payments yet</p>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Subscriptions Tab */}
          <TabsContent value="subscriptions">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <Package className="w-5 h-5 mr-2" />
                  Active Subscriptions
                </CardTitle>
                <CardDescription>Your current subscription packages and billing details</CardDescription>
              </CardHeader>
              <CardContent>
                {subscriptions.length === 0 ? (
                  <div className="text-center py-12">
                    <Package className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-500 mb-4">You don't have any active subscriptions yet.</p>
                    <Button onClick={() => navigate('/packages')} className="bg-indigo-600 hover:bg-indigo-700">
                      Browse Packages
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {subscriptions.map((sub) => (
                      <Card key={sub.id} className="border-2">
                        <CardContent className="p-6">
                          <div className="flex justify-between items-start">
                            <div className="flex-1">
                              <h3 className="text-xl font-bold text-gray-900 mb-2">
                                {sub.package_details?.name || 'Package'}
                              </h3>
                              <p className="text-gray-600 mb-4">{sub.package_details?.description}</p>
                              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                                <div>
                                  <p className="text-gray-500">Product</p>
                                  <p className="font-semibold">{sub.package_details?.product_name}</p>
                                </div>
                                <div>
                                  <p className="text-gray-500">Amount</p>
                                  <p className="font-semibold">${sub.amount.toFixed(2)}</p>
                                </div>
                                <div>
                                  <p className="text-gray-500">Billing</p>
                                  <p className="font-semibold capitalize">{sub.billing_cycle}</p>
                                </div>
                                <div>
                                  <p className="text-gray-500">Next Billing</p>
                                  <p className="font-semibold text-orange-600">{getNextBillingDate(sub)}</p>
                                </div>
                              </div>
                            </div>
                            <div className="ml-4">
                              <span className="px-3 py-1 bg-green-100 text-green-800 text-xs font-medium rounded-full">
                                Active
                              </span>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Payments & Invoices Tab */}
          <TabsContent value="payments">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <FileText className="w-5 h-5 mr-2" />
                  Payment History & Invoices
                </CardTitle>
                <CardDescription>View and download your payment invoices</CardDescription>
              </CardHeader>
              <CardContent>
                {transactions.length === 0 ? (
                  <p className="text-center text-gray-500 py-8">No payment history yet.</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b bg-gray-50">
                          <th className="text-left p-3 text-sm font-semibold text-gray-700">Date</th>
                          <th className="text-left p-3 text-sm font-semibold text-gray-700">Package</th>
                          <th className="text-left p-3 text-sm font-semibold text-gray-700">Billing</th>
                          <th className="text-left p-3 text-sm font-semibold text-gray-700">Amount</th>
                          <th className="text-left p-3 text-sm font-semibold text-gray-700">Status</th>
                          <th className="text-left p-3 text-sm font-semibold text-gray-700">Invoice</th>
                        </tr>
                      </thead>
                      <tbody>
                        {transactions.map((trans) => (
                          <tr key={trans.id} className="border-b hover:bg-gray-50">
                            <td className="p-3 text-sm text-gray-900">
                              {formatDate(trans.created_at)}
                            </td>
                            <td className="p-3 text-sm font-medium">
                              {trans.metadata?.package_name || 'N/A'}
                            </td>
                            <td className="p-3 text-sm capitalize">
                              {trans.billing_cycle}
                            </td>
                            <td className="p-3 text-sm font-semibold text-gray-900">
                              ${trans.amount.toFixed(2)} {trans.currency.toUpperCase()}
                            </td>
                            <td className="p-3 text-sm">
                              <span className="px-2 py-1 bg-green-100 text-green-800 text-xs font-medium rounded-full">
                                Paid
                              </span>
                            </td>
                            <td className="p-3 text-sm">
                              <div className="flex gap-2">
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleViewInvoice(trans.id)}
                                >
                                  <Eye className="w-4 h-4 mr-1" />
                                  View
                                </Button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Settings Tab */}
          <TabsContent value="settings">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <Settings className="w-5 h-5 mr-2" />
                  Account Settings
                </CardTitle>
                <CardDescription>Manage your account preferences</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  <div className="border-b pb-6">
                    <h3 className="text-lg font-semibold mb-4">Set Password</h3>
                    <p className="text-sm text-gray-600 mb-4">
                      Set a password to secure your account. Without a password, you can only access your account via email login links.
                    </p>
                    <div className="space-y-4 max-w-md">
                      <div>
                        <Label>New Password</Label>
                        <Input
                          type="password"
                          value={newPassword}
                          onChange={(e) => setNewPassword(e.target.value)}
                          placeholder="Enter new password"
                        />
                      </div>
                      <div>
                        <Label>Confirm Password</Label>
                        <Input
                          type="password"
                          value={confirmPassword}
                          onChange={(e) => setConfirmPassword(e.target.value)}
                          placeholder="Confirm new password"
                        />
                      </div>
                      <Button onClick={handleSetPassword} className="bg-indigo-600 hover:bg-indigo-700">
                        Set Password
                      </Button>
                    </div>
                  </div>

                  <div>
                    <h3 className="text-lg font-semibold mb-2">Account Information</h3>
                    <div className="space-y-2 text-sm">
                      <p><strong>Name:</strong> {userName}</p>
                      <p><strong>Email:</strong> {userEmail}</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Invoice Modal */}
      <Dialog open={showInvoiceModal} onOpenChange={setShowInvoiceModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Invoice Details</DialogTitle>
            <DialogDescription>Transaction invoice and details</DialogDescription>
          </DialogHeader>
          {selectedInvoice && (
            <div className="space-y-4">
              <div className="bg-gray-50 p-6 rounded-lg">
                <div className="flex justify-between mb-4">
                  <div>
                    <h3 className="font-bold text-lg">INVOICE</h3>
                    <p className="text-sm text-gray-600">ID: {selectedInvoice.transaction.id}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-gray-600">Date</p>
                    <p className="font-semibold">{formatDate(selectedInvoice.transaction.created_at)}</p>
                  </div>
                </div>
                
                <div className="border-t border-b py-4 my-4">
                  <p className="text-sm text-gray-600 mb-1">Bill To:</p>
                  <p className="font-semibold">{selectedInvoice.user.name}</p>
                  <p className="text-sm">{selectedInvoice.user.email}</p>
                </div>

                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Package:</span>
                    <span className="font-semibold">{selectedInvoice.transaction.metadata?.package_name || 'N/A'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Billing Cycle:</span>
                    <span className="font-semibold capitalize">{selectedInvoice.transaction.billing_cycle}</span>
                  </div>
                  <div className="flex justify-between text-lg font-bold border-t pt-2 mt-2">
                    <span>Total Amount:</span>
                    <span className="text-green-600">${selectedInvoice.transaction.amount.toFixed(2)} {selectedInvoice.transaction.currency.toUpperCase()}</span>
                  </div>
                </div>

                <div className="mt-4 pt-4 border-t">
                  <span className="px-3 py-1 bg-green-100 text-green-800 text-xs font-medium rounded-full">
                    ✓ PAID
                  </span>
                </div>
              </div>

              <Button onClick={downloadInvoice} className="w-full">
                <Download className="w-4 h-4 mr-2" />
                Download Invoice
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
